<template>
    <v-container>
        <v-form ref="form" lazy-validation v-if="user">

            <label class="font-weight-thin display-1">Anmeldedaten</label>
            <p class="caption">Mit den folgenden Anmeldedaten kann sich ein Benutzer anmelden.</p>

            <v-row>
                <v-col cols="12" md="6" lg="5">
                    <v-text-field name="email" :label="$t('app.email')" type="email"
                                  v-model="user.email" :rules="emailRules" :disabled="editMode">
                    </v-text-field>
                </v-col>
                <v-col md="6" lg="5" offset-lg="2" v-if="!editMode">
                    <v-text-field name="password" :label="$t('app.password')" counter
                                  v-model="user.password" :rules="passwordRules"
                                  :append-icon="passwordShow ? 'mdi-eye' : 'mdi-eye-off'"
                                  :type="passwordShow ? 'text' : 'password'"
                                  @click:append="passwordShow = !passwordShow">
                    </v-text-field>
                </v-col>
            </v-row>

            <label class="font-weight-thin display-1">Persönliche Daten</label>
            <p class="caption">Fügen Sie folgende persönliche Daten dem Benutzern hinzu.</p>

            <v-row>
                <v-col cols="12" md="6" lg="5">
                    <v-text-field name="firstName" :label="$t('key.firstName')" type="text"
                                  v-model="user.firstName" :rules="firstNameRules">
                    </v-text-field>
                </v-col>
                <v-col md="6" lg="5" offset-lg="2">
                    <v-text-field name="lastName" :label="$t('key.lastName')" type="text"
                                  v-model="user.lastName" :rules="lastNameRules">
                    </v-text-field>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" md="6" lg="5">
                    <v-text-field name="mobileNumber" :label="$t('key.mobilePhoneNumber')" type="number" placeholder="06641234567"
                                  v-model="user.mobileNumber" :rules="mobilePhoneRules">
                    </v-text-field>
                </v-col>
                <v-col md="6" lg="5" offset-lg="2">
                    <v-select name="role" :label="$t('app.role')" item-text="i18n" item-value="value"
                              v-model="user.role" :items="filteredRoles">
                    </v-select>
                </v-col>
            </v-row>

            <label class="font-weight-thin display-1">Maschinenring</label>
            <p class="caption">Weisen Sie den Benutzer einen Maschinenring zu.</p>

            <v-row>
                <v-col cols="12" md="6" lg="5">
                    <v-select name="province" :label="$t('key.province')" item-text="text" item-value="value"
                              v-model="user.province" :items="filteredProvinces" >
                    </v-select>
                </v-col>
                <v-col md="6" lg="2" offset-lg="2">
                    <v-text-field name="pool" :label="$t('key.pool')" type="number"
                                  v-model="user.pool" :rules="poolRules" :counter="3">
                    </v-text-field>
                </v-col>
            </v-row>
        </v-form>

        <v-row>
            <v-spacer></v-spacer>
            <v-btn dark class="green lighten-1 ma-2" min-width="100px" @click="onSubmit">{{$t('app.save')}}</v-btn>
            <v-btn class="ma-2" min-width="100px" @click="onCancel">{{$t('app.cancel')}}</v-btn>
        </v-row>
    </v-container>
</template>

<script>
    import {Provinces, Roles} from '../../_helpers';

    export default {
        name: 'UserForm',
        props: ['userId', 'editMode'],
        computed: {
            user() {
                let user = this.findUser(this.userId)

                return user ? user : {
                    //uuid: uuid.v4(), -> uid already exist
                    email: null,
                    password: null,
                    firstName: null,
                    lastName: null,
                    mobileNumber: null,
                    role: Roles.ROLE_3.value,
                    province: Provinces.PROVINCE_9.value,
                    pool: null,
                }
            },
            // Depending on user's role, return all (SUPERADMIN) or only the
            // province were user belongs to
            filteredProvinces() {
                let userProfile = this.$store.getters.userProfile

                // In case of a page refresh, the user-profile is not yet
                // loaded and an empty list is returned
                if (!userProfile) {
                    return []
                }

                if (userProfile.role === Roles.ROLE_1.value) {
                    return Provinces.list()
                } else {
                    return Provinces.itemByValue(userProfile.province)
                }
            },
            // Depending on user's role, return all (SUPERADMIN) or only those
            // without SUPERADMIN role
            filteredRoles() {
                let userProfile = this.$store.getters.userProfile

                // In case of a page refresh, the user-profile is not yet
                // loaded and an empty list is returned
                if (!userProfile) {
                    return []
                }

                if (userProfile.role === Roles.ROLE_1.value) {
                    return this.localizeEnums(Roles.list())
                } else {
                    let filteredRoles = Roles.list().slice(1, Roles.list().length) // remove SUPERADMIN role
                    return this.localizeEnums(filteredRoles)
                }
            },
            emailRules() {
                return [
                    v => !!v || this.$t('key.emailRequired'),
                    v => /.+@.+\..+/.test(v) || this.$t('key.emailInvalid')
                ]
            },
            passwordRules() {
                return [
                    v => !!v || this.$t('key.passwordRequired'),
                    v => (v && v.length >= 6) || this.$t('key.passwordInvalid'),
                ]
            },
            firstNameRules() {
                return [
                    v => !!v || this.$t('key.firstNameRequired')
                ]
            },
            lastNameRules() {
                return [
                    v => !!v || this.$t('key.lastNameRequired')
                ]
            },
            mobilePhoneRules() {
                return [
                    v => !!v || this.$t('key.mobilePhoneRequired')
                ]
            },
            poolRules() {
                return [
                    v => !!v || this.$t('key.poolRequired'),
                    v => (v && v.length == 3) || this.$t('key.poolInvalid'),
                ]
            }
        },
        data: () => ({
            passwordShow: false,
        }),
        methods: {
            findUser(userId) {
                let user = this.$store.state.user.user
                return (user && user.user_id === userId) ? user : null
            },
            onSubmit() {
                if (this.$refs.form.validate()) {
                    this.$store.dispatch('applyUser', this.user)
                } else {
                    console.log(`Form invalid: ${JSON.stringify(this.$refs.form)}`)
                }
            },
            onCancel() {
                this.$router.push({name: 'Users'})
            },
            localizeEnums(values) {
                let list = []
                for (let obj of values) {
                    // Add i18n-property to object
                    obj.i18n = this.$t('key.'+obj.value)

                    // Push modified object to list
                    list.push(obj)
                }
                return list
            }
        },
        mounted() {
            this.$store.dispatch('getUserById', this.userId);
        }
    };
</script>
